import React, { useCallback, useEffect, useState } from 'react'
import { CFormSelect } from '@coreui/react-pro'
import FixtureVotingService from 'src/service/FixtureVotingService'
import LoadError from './LoadError'
import PlayerVotesTable from './PlayerVotesTable'

const teamLabel = (team) => [team.grade_name, team.team_name].filter(Boolean).join(' · ')

// One team at a time: pick it from the list of teams that have votes, then see its players.
const TeamVotesTab = ({ teams, loading, error, onRetry }) => {
  const [teamId, setTeamId] = useState('')
  const [detail, setDetail] = useState({ players: [], options: [], loading: false, error: false })

  // Default to the first team once the list arrives, and drop a team that has gone away.
  useEffect(() => {
    if (!teams.some((team) => String(team.team_id) === teamId)) {
      setTeamId(teams.length ? String(teams[0].team_id) : '')
    }
  }, [teams, teamId])

  const loadTeam = useCallback(() => {
    if (!teamId) return undefined
    let current = true
    setDetail({ players: [], options: [], loading: true, error: false })
    FixtureVotingService.teamFixtureVotingDetail(teamId)
      .then((res) => {
        if (!current) return
        if (res.success) {
          setDetail({
            players: res.data || [],
            options: res.voting_list || [],
            loading: false,
            error: false,
          })
        } else {
          setDetail((d) => ({ ...d, loading: false, error: true }))
        }
      })
      .catch(() => current && setDetail((d) => ({ ...d, loading: false, error: true })))
    // Switching teams quickly must not let the previous team's players land in the table.
    return () => {
      current = false
    }
  }, [teamId])

  useEffect(() => loadTeam(), [loadTeam])

  if (error) return <LoadError message="We couldn't load the teams." onRetry={onRetry} />

  if (!loading && teams.length === 0) {
    return (
      <div className="vt-empty">No votes yet - teams appear here once members start voting.</div>
    )
  }

  return (
    <PlayerVotesTable
      players={detail.players}
      options={detail.options}
      loading={loading || detail.loading}
      error={detail.error}
      onRetry={loadTeam}
      errorMessage="We couldn't load this team's votes."
      noItemsLabel="No players in this team have been voted for yet"
      trailing={
        <CFormSelect
          className="vt-team-select"
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          aria-label="Team"
          disabled={loading}
        >
          {teams.map((team) => (
            <option key={team.team_id} value={team.team_id}>
              {teamLabel(team)}
            </option>
          ))}
        </CFormSelect>
      }
    />
  )
}

export default TeamVotesTab
